import { StrictMode, Suspense, lazy, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import '@fontsource-variable/inter';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import '@fontsource/fredoka/500.css';
import '@fontsource/fredoka/600.css';
import '@fontsource/fredoka/700.css';
import './styles/app.css';
import { useNav } from './nav';
import { BRAND } from './brand';
import { pageShown } from './analytics';
import { installWritable } from './editor/opfswrite';

// (files in the browser's own storage written the same way everywhere, Safari on an iPad included)
installWritable();

// the drawing benchmark, from the console on any computer (it loads only when it's run; nothing is sent anywhere)
(window as unknown as { cutroomBench: unknown }).cutroomBench = (o?: Record<string, unknown>) =>
  import('./editor/bench').then((m) => m.runBench(o));

const Landing = lazy(() => import('./landing/Landing'));
// the app (dashboard and editor) loads only when it's opened, and so does the manual
const App = lazy(() => import('./App'));
const Help = lazy(() => import('./help/Help'));

/** Titles and captions are drawn in canvas fonts: wait for them so the first frame already matches the export. */
const fontsReady = Promise.all(
  ['500 20px Fredoka', '600 20px Fredoka', '700 20px Fredoka', '400 20px "Inter Variable"'].map((f) =>
    document.fonts.load(f).catch(() => null),
  ),
);

function Root() {
  const page = useNav((s) => s.page);
  useEffect(() => {
    document.body.dataset.page = page;
    if (page === 'landing') document.title = `${BRAND.name} — free, private video editing in your browser`;
    // (after the title's set; the manual sets its own a moment later)
    const id = setTimeout(() => pageShown(page), 300);
    return () => clearTimeout(id);
  }, [page]);
  return (
    <Suspense fallback={<div className="boot" aria-busy="true" />}>
      {page === 'landing' ? <Landing /> : page === 'help' ? <Help /> : <App />}
    </Suspense>
  );
}

// works offline once opened (the built site only: in development every file changes as it's edited)
if (import.meta.env.PROD && 'serviceWorker' in navigator)
  window.addEventListener('load', () => void navigator.serviceWorker.register('/sw.js').catch(() => null));

const root = document.getElementById('root');
if (!root) throw new Error('Missing #root');
void fontsReady.then(() =>
  createRoot(root).render(
    <StrictMode>
      <Root />
    </StrictMode>,
  ),
);
